import { useEffect, useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import {
  MASCOT_AGE_GROUP_OPTIONS,
  MASCOT_GENDER_OPTIONS,
  MASCOT_HEIGHT_OPTIONS,
  appearanceProfileBadges,
  appearanceProfileFormState,
  appearanceProfilePayloadFromForm,
  appearanceProfileSummary,
} from '@/lib/mascotAnimation';

import { TwinneeLogo } from '@/components/TwinneeLogo';
export function MascotCharacterTraitsSection({
  projectId,
  character,
  onUpdated,
  disabled = false,
  className = '',
}) {
  const [saving, setSaving] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);
  const [form, setForm] = useState(() => appearanceProfileFormState(character?.appearance_profile));

  useEffect(() => {
    setForm(appearanceProfileFormState(character?.appearance_profile));
  }, [character?.appearance_profile, character?.id]);

  if (!character?.reference_url) return null;

  const profile = character.appearance_profile;
  const hasTraits = profile?.analysis_status === 'completed';
  const failed = profile?.analysis_status === 'failed';
  const userEdited = profile?.analysis_source === 'user_edited';

  const openQuick = () => {
    setForm(appearanceProfileFormState(profile));
    setQuickOpen(true);
  };

  const openFull = () => {
    setForm(appearanceProfileFormState(profile));
    setFullOpen(true);
  };

  const saveProfile = async (mode) => {
    if (!projectId || !character?.id) return;
    const payload = appearanceProfilePayloadFromForm(form, mode);
    if (mode === 'consistency_only' && !payload.consistency_directive) {
      toast.error('Enter a consistency note');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/mascot/projects/${projectId}/characters/${character.id}/appearance-profile`,
        payload,
      );
      onUpdated?.(data.project);
      setQuickOpen(false);
      setFullOpen(false);
      toast.success(mode === 'consistency_only' ? 'Consistency note saved' : 'Traits saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save traits');
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className={className}>
      {hasTraits || userEdited ? (
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] font-medium text-sky-600">
              {userEdited ? 'Character traits (edited)' : 'Detected traits (used in all generations)'}
            </p>
            <div className="flex flex-wrap gap-1 justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[10px]"
                disabled={disabled || saving}
                onClick={openQuick}
              >
                <Pencil className="w-3 h-3 mr-1" />
                Edit note
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[10px]"
                disabled={disabled || saving}
                onClick={openFull}
              >
                <TwinneeLogo className="w-3 h-3 mr-1" />
                Edit all traits
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-1">
            {appearanceProfileBadges(profile).map((badge) => (
              <Badge key={`${character.id}-${badge}`} variant="secondary" className="text-[10px] rounded-full px-2 py-0">
                {badge}
              </Badge>
            ))}
          </div>
          {appearanceProfileSummary(profile) ? (
            <p className="text-[10px] text-[var(--text-tertiary)] leading-relaxed line-clamp-3">
              {appearanceProfileSummary(profile)}
            </p>
          ) : null}
        </div>
      ) : failed ? (
        <div className="mt-3 space-y-2">
          <p className="text-[10px] text-amber-600">
            Trait detection unavailable — add traits manually or edit the consistency note.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" className="h-7 text-[10px]" disabled={disabled || saving} onClick={openQuick}>
              Add consistency note
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-7 text-[10px]" disabled={disabled || saving} onClick={openFull}>
              Add all traits
            </Button>
          </div>
        </div>
      ) : null}

      <Dialog open={quickOpen} onOpenChange={setQuickOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit consistency note</DialogTitle>
            <DialogDescription>
              Quick edit for {character.name}. This text is injected into sheet, Seedance, and animation prompts.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor={`consistency-${character.id}`}>Consistency note</Label>
            <Textarea
              id={`consistency-${character.id}`}
              value={form.consistency_directive}
              onChange={(e) => setField('consistency_directive', e.target.value)}
              placeholder="e.g. Adult woman with medium brown skin, long wavy black hair, navy blazer. Keep identical in every shot."
              className="min-h-[140px]"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setQuickOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={() => saveProfile('consistency_only')} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={fullOpen} onOpenChange={setFullOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit all traits</DialogTitle>
            <DialogDescription>
              Full trait editor for {character.name}. Leave the consistency note blank to rebuild it from these fields.
            </DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Gender</Label>
              <Select value={form.gender} onValueChange={(v) => setField('gender', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MASCOT_GENDER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Age group</Label>
              <Select value={form.age_group} onValueChange={(v) => setField('age_group', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MASCOT_AGE_GROUP_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Estimated age (years)</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={form.estimated_age_years}
                onChange={(e) => setField('estimated_age_years', e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-2">
              <Label>Height impression</Label>
              <Select value={form.height_impression} onValueChange={(v) => setField('height_impression', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MASCOT_HEIGHT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Ethnicity / heritage</Label>
              <Input value={form.ethnicity_or_heritage} onChange={(e) => setField('ethnicity_or_heritage', e.target.value)} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Skin tone</Label>
              <Input value={form.skin_tone} onChange={(e) => setField('skin_tone', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Hair color</Label>
              <Input value={form.hair_color} onChange={(e) => setField('hair_color', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Hair style</Label>
              <Input value={form.hair_style} onChange={(e) => setField('hair_style', e.target.value)} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Hair length</Label>
              <Input value={form.hair_length} onChange={(e) => setField('hair_length', e.target.value)} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Facial features</Label>
              <Input value={form.facial_features} onChange={(e) => setField('facial_features', e.target.value)} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Body type</Label>
              <Input value={form.body_type} onChange={(e) => setField('body_type', e.target.value)} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Clothing style</Label>
              <Input value={form.clothing_style} onChange={(e) => setField('clothing_style', e.target.value)} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Expression default</Label>
              <Input value={form.expression_default} onChange={(e) => setField('expression_default', e.target.value)} />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Distinctive features (one per line)</Label>
              <Textarea
                value={form.distinctive_features}
                onChange={(e) => setField('distinctive_features', e.target.value)}
                className="min-h-[72px]"
                placeholder="dimples when smiling"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Accessories (one per line)</Label>
              <Textarea
                value={form.accessories}
                onChange={(e) => setField('accessories', e.target.value)}
                className="min-h-[72px]"
                placeholder="thin gold hoop earrings"
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Consistency note (optional override)</Label>
              <Textarea
                value={form.consistency_directive}
                onChange={(e) => setField('consistency_directive', e.target.value)}
                className="min-h-[96px]"
                placeholder="Leave blank to auto-build from the fields above"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setFullOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" onClick={() => saveProfile('full')} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save traits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
